import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import React, { useCallback, startTransition, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

import { useMakeModel } from '@/hooks/useMakeModel';
import { VehicleType } from '@/types/car';
import { useTranslationV2 } from '@/hooks/useTranslationV2';
import { SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';

export interface AdvancedFilterValues {
  mileageFrom?: string;
  mileageTo?: string;
  powerFrom?: string;
  powerTo?: string;
  euroNorm?: string;
  doors?: string;
  bodyType?: string;
  color?: string;
  driveType?: string;
  conditionTechnical?: string;
  sellerType?: string;
  location?: string;
  features?: string[];
}

interface VehiclesFilterBarProps {
  selectedType?: VehicleType;
  make: string;
  model: string;
  priceMax: string;
  yearMin: string;
  onTypeChange: (type: VehicleType | '') => void;
  onMakeChange: (make: string) => void;
  onModelChange: (model: string) => void;
  onPriceMaxChange: (price: string) => void;
  onYearMinChange: (year: string) => void;
  onSearch: () => void;
  advanced: AdvancedFilterValues;
  onAdvancedChange: (patch: Partial<AdvancedFilterValues>) => void;
}

const PRICE_OPTIONS = [
  { label: '5.000 €', value: '5000' },
  { label: '10.000 €', value: '10000' },
  { label: '15.000 €', value: '15000' },
  { label: '20.000 €', value: '20000' },
  { label: '30.000 €', value: '30000' },
  { label: '50.000 €', value: '50000' },
  { label: '100.000 €', value: '100000' },
];

const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { label: String(year), value: String(year) };
});

const EURO_OPTIONS = ['Euro 6', 'Euro 5', 'Euro 4', 'Euro 3'];
const DOORS_OPTIONS = ['2', '3', '4', '5'];
const BODY_OPTIONS = ['suv', 'sedan', 'hatchback', 'estate', 'coupe', 'cabrio', 'minivan', 'pickup'];
const COLOR_OPTIONS = ['alb', 'negru', 'gri', 'argintiu', 'albastru', 'rosu', 'verde', 'galben', 'maro', 'portocaliu', 'auriu'];
const DRIVE_OPTIONS = ['fwd', 'rwd', 'awd'];
const CONDITION_OPTIONS = ['excellent', 'very_good', 'good', 'average'];
const SELLER_OPTIONS = ['dealer', 'private'];
const FEATURE_OPTIONS = [
  'ac', 'climate_control', 'navigation', 'cruise_control', 'parking_sensors_rear',
  'led_headlights', 'alloy_wheels', 'bluetooth', 'isofix', 'lane_assist',
];

export const VehiclesFilterBar: React.FC<VehiclesFilterBarProps> = ({
  selectedType,
  make,
  model,
  priceMax,
  yearMin,
  onTypeChange,
  onMakeChange,
  onModelChange,
  onPriceMaxChange,
  onYearMinChange,
  onSearch,
  advanced,
  onAdvancedChange,
}) => {
  const { makes, getModelsForMake, loading } = useMakeModel();
  const { t } = useTranslationV2('filters');
  const availableModels = make ? getModelsForMake(make) : [];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const CATEGORY_OPTIONS: { label: string; value: VehicleType }[] = [
    { label: t('cat_cars', 'Autoturisme'), value: 'car' },
    { label: t('cat_trucks', 'Camioane'), value: 'truck' },
    { label: t('cat_vans', 'Autoutilitare'), value: 'van' },
    { label: t('cat_motorcycles', 'Motociclete'), value: 'motorcycle' },
    { label: t('cat_boats', 'Ambarcațiuni'), value: 'boat' },
    { label: t('cat_construction', 'Utilaje'), value: 'construction' },
    { label: t('cat_agricultural', 'Tractoare'), value: 'agricultural' },
    { label: t('cat_atv', 'ATV'), value: 'atv' },
  ];

  const handleMakeChange = useCallback((v: string) => {
    startTransition(() => {
      onMakeChange(v);
      onModelChange('');
    });
  }, [onMakeChange, onModelChange]);

  const toggleFeature = (key: string) => {
    const cur = advanced.features ?? [];
    const next = cur.includes(key) ? cur.filter((f) => f !== key) : [...cur, key];
    onAdvancedChange({ features: next });
  };

  const selectCls = 'h-11 rounded-lg bg-background border-border/60';

  // Caută: închide panourile de filtre ca rezultatele să fie vizibile imediat
  const handleSearch = useCallback(() => {
    setMobileOpen(false);
    setAdvancedOpen(false);
    onSearch();
  }, [onSearch]);

  const renderSelect = (
    value: string | undefined,
    placeholder: string,
    options: { label: string; value: string }[],
    onChange: (v: string) => void,
  ) => (
    <Select value={value || ''} onValueChange={(v) => onChange(v === '__any__' ? '' : v)}>
      <SelectTrigger className={selectCls}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="z-50 bg-popover max-h-[300px]">
        <SelectItem value="__any__">{t('any', 'Oricare')}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="w-full rounded border border-border bg-card p-3 md:p-4">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 md:hidden">
        <Button type="button" variant="outline" onClick={() => setMobileOpen((open) => !open)} className="min-w-0 justify-start">
          <SlidersHorizontal className="h-4 w-4 shrink-0" />
          <span className="truncate">Filtre</span>
        </Button>
        <Button onClick={handleSearch} className="bronze-action shrink-0 font-semibold normal-case tracking-normal">
          <Search className="h-4 w-4 shrink-0" />
          Caută
        </Button>
      </div>
      <div className={`${mobileOpen ? 'grid' : 'hidden'} mt-3 grid-cols-2 gap-2 md:mt-0 md:flex md:flex-wrap md:gap-3 md:items-end`}>
        {/* Categorie */}
        <div className="flex-1 min-w-[120px]">
          <Select value={selectedType || ''} onValueChange={(v) => onTypeChange(v as VehicleType)}>
            <SelectTrigger className={selectCls}>
              <SelectValue placeholder={t('category', 'Categorie')} />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover">
              {CATEGORY_OPTIONS.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Marcă */}
        <div className="flex-1 min-w-[120px]">
          <Select value={make} onValueChange={handleMakeChange} disabled={loading}>
            <SelectTrigger className={selectCls}>
              <SelectValue placeholder={loading ? t('loading_makes', 'Se încarcă...') : t('make', 'Marcă')} />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover max-h-[300px]">
              {makes.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label} ({m.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Preț */}
        <div className="flex-1 min-w-[100px]">
          <Select value={priceMax} onValueChange={onPriceMaxChange}>
            <SelectTrigger className={selectCls}>
              <SelectValue placeholder={t('price', 'Preț')} />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover">
              {PRICE_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{t('up_to', 'Până la')} {p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* An */}
        <div className="flex-1 min-w-[90px]">
          <Select value={yearMin} onValueChange={onYearMinChange}>
            <SelectTrigger className={selectCls}>
              <SelectValue placeholder={t('year', 'An')} />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover max-h-[300px]">
              {YEAR_OPTIONS.map((y) => (
                <SelectItem key={y.value} value={y.value}>{t('from_year', 'Din')} {y.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* CAUTĂ Button — editorial cream inverted CTA */}
        <Button
          onClick={handleSearch}
          className="bronze-action hidden h-11 px-6 font-semibold normal-case tracking-normal shadow-none md:inline-flex"
        >
          <Search className="h-4 w-4 mr-2" />
          {t('search', 'Caută')}
        </Button>
      </div>

      {/* Mai multe filtre — toggle */}
      <div className={`${mobileOpen ? 'block' : 'hidden'} md:block mt-2`}>
        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {advancedOpen ? t('fewer_filters', 'Mai puține filtre') : t('more_filters', 'Mai multe filtre')}
        </button>
      </div>

      {/* Panou filtre avansate */}
      {advancedOpen && (
        <div className="mt-3 space-y-5 border-t border-border/60 pt-4">
          {/* Tehnice */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t('section_technical', 'Tehnice')}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input
                type="number"
                inputMode="numeric"
                placeholder={t('mileage_min', 'Kilometraj minim')}
                value={advanced.mileageFrom ?? ''}
                onChange={(e) => onAdvancedChange({ mileageFrom: e.target.value })}
                className="h-11 rounded-lg bg-background border-border/60"
              />
              <Input
                type="number"
                inputMode="numeric"
                placeholder={t('mileage_max', 'Kilometraj maxim')}
                value={advanced.mileageTo ?? ''}
                onChange={(e) => onAdvancedChange({ mileageTo: e.target.value })}
                className="h-11 rounded-lg bg-background border-border/60"
              />
              <Input
                type="number"
                inputMode="numeric"
                placeholder={`${t('power_min', 'Putere minimă')} (CP)`}
                value={advanced.powerFrom ?? ''}
                onChange={(e) => onAdvancedChange({ powerFrom: e.target.value })}
                className="h-11 rounded-lg bg-background border-border/60"
              />
              <Input
                type="number"
                inputMode="numeric"
                placeholder={`${t('power_max', 'Putere maximă')} (CP)`}
                value={advanced.powerTo ?? ''}
                onChange={(e) => onAdvancedChange({ powerTo: e.target.value })}
                className="h-11 rounded-lg bg-background border-border/60"
              />
              {renderSelect(
                advanced.euroNorm,
                t('euro_norm', 'Normă Euro'),
                EURO_OPTIONS.map((e) => ({ label: e, value: e })),
                (v) => onAdvancedChange({ euroNorm: v }),
              )}
              {renderSelect(
                advanced.doors,
                t('doors', 'Uși'),
                DOORS_OPTIONS.map((d) => ({ label: d, value: d })),
                (v) => onAdvancedChange({ doors: v }),
              )}
            </div>
          </div>

          {/* Aspect și stare */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t('section_appearance', 'Aspect și stare')}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {renderSelect(
                advanced.bodyType,
                t('body_type', 'Caroserie'),
                BODY_OPTIONS.map((b) => ({ label: t(`body_${b}`, b), value: b })),
                (v) => onAdvancedChange({ bodyType: v }),
              )}
              {renderSelect(
                advanced.color,
                t('color', 'Culoare'),
                COLOR_OPTIONS.map((c) => ({ label: t(`color_${c}`, c), value: c })),
                (v) => onAdvancedChange({ color: v }),
              )}
              {renderSelect(
                advanced.driveType,
                t('drive_type', 'Tracțiune'),
                DRIVE_OPTIONS.map((d) => ({ label: t(`drive_${d}`, d.toUpperCase()), value: d })),
                (v) => onAdvancedChange({ driveType: v }),
              )}
              {renderSelect(
                advanced.conditionTechnical,
                t('condition_technical', 'Stare tehnică'),
                CONDITION_OPTIONS.map((c) => ({ label: t(`cond_${c}`, c), value: c })),
                (v) => onAdvancedChange({ conditionTechnical: v }),
              )}
            </div>
          </div>

          {/* Vânzător și dotări */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t('section_seller', 'Vânzător și dotări')}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {renderSelect(
                advanced.sellerType,
                t('seller_type', 'Tip vânzător'),
                SELLER_OPTIONS.map((s) => ({ label: t(`seller_${s}`, s), value: s })),
                (v) => onAdvancedChange({ sellerType: v }),
              )}
              <Input
                type="text"
                placeholder={t('city', 'Oraș')}
                value={advanced.location ?? ''}
                onChange={(e) => onAdvancedChange({ location: e.target.value })}
                className="h-11 rounded-lg bg-background border-border/60"
              />
            </div>
            <div className="mt-3">
              <div className="text-xs font-medium mb-2">{t('features', 'Dotări')}</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                {FEATURE_OPTIONS.map((f) => (
                  <label key={f} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={(advanced.features ?? []).includes(f)}
                      onCheckedChange={() => toggleFeature(f)}
                    />
                    <span>{t(`feat_${f}`, f)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Căutare cu filtrele avansate */}
          <div className="flex justify-end">
            <Button onClick={handleSearch} className="bronze-action h-11 px-6 font-semibold normal-case tracking-normal shadow-none">
              <Search className="h-4 w-4 mr-2" />
              {t('search', 'Caută')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
